import { useState } from 'react';
import {
  Box,
  Drawer,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { useRouter } from 'next/router';

import { PATH_SUBSCRIBER_SUBDOMAIN } from '@subflow-frontend/routes/paths';
import { useSubdomain } from '@subflow-frontend/sections/subscriber';
import OrgAvatar from '@subflow-frontend/components/avatars/OrgAvatar';
import useResponsive from '@subflow-frontend/hooks/useResponsive';

const SIDEBAR_WIDTH = 335;

export const OrgMenu = ({ orgs, handleClose }) => {
  const router = useRouter();
  const { subdomain } = useSubdomain();

  const handleOrgClick = (org) => {
    router.push(PATH_SUBSCRIBER_SUBDOMAIN(org.subDomain).dashboard);
    handleClose();
  }

  return (
    <>
      <Stack
        sx={{
          p: '8px',
          gap: '12px' ,
          background: '#F7F9FC',
        }}
      >
        {orgs?.map((org) => (
          <Stack 
            key={org.id}
            direction="row" 
            alignItems="center" 
            spacing={1} 
            sx={{
              cursor: 'pointer',
              background: org.subDomain === subdomain ? '#3B64FF' : '#F7F9FC',
              p: '0.5rem',
              borderRadius: '1rem',
            }}
            onClick={() => handleOrgClick(org)}
          >
            <OrgAvatar merchant={org} />
            <Box sx={{ minWidth: '100px' }}>
              <Typography variant="subtitle2" noWrap>
                {org.businessName}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </>
  );
};

export default function OrgPopover({ orgs }) {
  const [open, setOpen] = useState<HTMLElement | null>(null);

  const { subdomain } = useSubdomain();
  const isDesktop = useResponsive('up', 'md');
  const currentOrg = orgs?.find(org => org.subDomain === subdomain);
  const restOrgs = orgs?.filter(org => org.subDomain != subdomain);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  return (
    <>
      <Box
        onClick={handleOpen}
      >
        <Stack 
          direction="row" 
          alignItems="center" 
          spacing={1}
          sx={{
            color: 'text.primary',
            cursor: 'pointer',
            '&:hover': {
              color: 'text.secondary',
            }
          }}
          onClick={handleOpen}
        >
          <Typography 
            variant="subtitle2"
            sx={{
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {currentOrg?.businessName}
          </Typography>
        </Stack>
      </Box>
      {isDesktop ? (
        <Popover
          open={Boolean(open)}
          anchorEl={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            top: '10px',
            borderRadius: '8px',
            boxShadow: '-13px 21px 49px -31px rgba(0, 0, 0, 0.13)',
          }}
        >
          <OrgMenu orgs={restOrgs} handleClose={handleClose} />
        </Popover>
      ) : (
        <Drawer
          ModalProps={{ keepMounted: true }}
          open={Boolean(open)}
          onClose={handleClose}
          sx={{
            '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH },
          }}
        >
          <Box sx={{ py: '2rem', px: '1rem' }}>
            <Typography 
              variant="subtitle2"
              sx={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                mb: '1rem',
              }}
            >
              Organizations
            </Typography>
            <OrgMenu orgs={orgs} handleClose={handleClose} />
          </Box>
        </Drawer>
      )}
    </>
  );
}
